import { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Control, useFormContext } from 'react-hook-form'
import { cn } from '../../../utils'
import AuftragCheckbox from './AuftragCheckbox'
import { BefehlFormField } from './BefehlFormField'

interface BefehlFormAuftragProps extends PropsWithChildren {
  auftragKennung: string
  displayKennung?: string
  label?: ReactNode
  fieldName: string
  control?: Control
  readOnly?: boolean
  disabled?: boolean
  testId: string
  className?: string
  activateEmptyFieldWarnings?: boolean
  onChange?: (checked: boolean) => void
}

interface AuftragUIProps {
  effektiveKennung: string
  checked: boolean
  onChange?: (checked: boolean) => void
  readOnly: boolean
  disabled: boolean
  testId: string
  className?: string
  auftragKennung: string
  auftragName: string
  children?: ReactNode
}

export default function BefehlFormAuftrag({
  auftragKennung,
  displayKennung,
  fieldName,
  control,
  readOnly = false,
  disabled = false,
  testId,
  className,
  children,
  activateEmptyFieldWarnings = false,
  onChange
}: Readonly<BefehlFormAuftragProps>) {
  const { t } = useTranslation('ui-befehle')
  const formContext = useFormContext()

  const effectiveControl = control ?? formContext?.control

  const visibleKennung = displayKennung ?? auftragKennung
  const auftragName = t('auftrag_with_kennung', { kennung: visibleKennung })

  const isNonInteractive = readOnly ?? disabled

  const renderAuftragUI = ({
    checked,
    onChange,
    readOnly,
    disabled,
    testId,
    className,
    auftragName,
    children
  }: AuftragUIProps): React.ReactElement => {
    return (
      <div
        className={cn(`flex items-baseline`, `break-inside-avoid`, className)}
        aria-readonly={readOnly ? 'true' : 'false'}
        aria-disabled={disabled ? 'true' : 'false'}
      >
        <div className="w-1.75 shrink-0" />

        <div className="w-1.75 shrink-0 flex justify-center items-baseline ml-0.5 mr-1">
          <AuftragCheckbox
            label={visibleKennung}
            checked={checked}
            onChange={(e) =>
              readOnly || disabled || !onChange
                ? undefined
                : onChange(e.target.checked)
            }
            readOnly={readOnly}
            disabled={disabled}
            data-testid={`${testId}-inner`}
            aria-label={
              checked
                ? t('auftrag_is_selected', { auftragName })
                : t('auftrag_is_not_selected', { auftragName })
            }
            role={isNonInteractive ? 'status' : undefined}
          />
        </div>

        <div className="flex-grow flex flex-col gap-1">
          {(isNonInteractive ? checked : true) && children}
        </div>
      </div>
    )
  }

  if (!effectiveControl) {
    throw new Error(
      `BefehlFormAuftrag: No form control available for field "${fieldName}". Make sure the component is used within a FormProvider or pass a control prop.`
    )
  }

  return (
    <BefehlFormField
      name={fieldName}
      control={effectiveControl}
      label={auftragName}
      readOnly={readOnly}
      disabled={disabled}
      activateEmptyWarning={activateEmptyFieldWarnings}
      useTextDisplayInReadOnly={false}
      data-testid={`${testId}-auftrag-field`}
      render={({ field, isDisabled }) => {
        if (isNonInteractive && !field.value) {
          return null
        }

        return renderAuftragUI({
          effektiveKennung: visibleKennung,
          checked: !!field.value,
          onChange: (checked) => {
            field.onChange(checked)
            onChange?.(checked)
          },
          readOnly,
          disabled: isDisabled,
          testId,
          className,
          auftragKennung: auftragKennung,
          auftragName,
          children
        })
      }}
    />
  )
}
